import { useRef, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "./Icon.tsx";
import { MiniMonthPicker } from "./MiniMonthPicker.tsx";
import type { WeekStart } from "../lib/calendar-prefs.ts";
import { useIsMobile } from "../lib/use-is-mobile.ts";

export type ViewPeriod = "day" | "week" | "month" | "agenda";
export type ViewMode = "resource" | "list";

interface Option<T> {
  value: T;
  label: string;
}

function PillGroup<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Option<T>[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div
      style={{
        display: "inline-flex",
        gap: 2,
        padding: 3,
        borderRadius: 10,
        background: "var(--paper-well)",
        boxShadow: "0 0 0 1px var(--rule)",
      }}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            className="zd-pill-btn"
            onClick={() => onChange(opt.value)}
            style={{
              padding: "6px 12px",
              fontSize: 12.5,
              fontWeight: 500,
              background: active ? "var(--ink)" : "transparent",
              color: active ? "var(--canvas)" : "var(--ink-soft)",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              transition: "background 120ms ease, color 120ms ease",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function getWeekDays(d: Date): Date[] {
  const start = new Date(d);
  start.setDate(d.getDate() - d.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    return day;
  });
}

function formatDateLabel(d: Date, period: ViewPeriod): string {
  if (period === "day") {
    return d.toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }
  if (period === "week" || period === "agenda") {
    const days = getWeekDays(d);
    const s = days[0].toLocaleDateString(undefined, { month: "short", day: "numeric" });
    const e = days[6].toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${s} – ${e}`;
  }
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

interface Props {
  currentDate: Date;
  setCurrentDate: (d: Date) => void;
  viewPeriod: ViewPeriod;
  setViewPeriod: (v: ViewPeriod) => void;
  viewMode: ViewMode;
  setViewMode: (v: ViewMode) => void;
  zoom: 1 | 2 | 4;
  setZoom: (z: 1 | 2 | 4) => void;
  loading: boolean;
  onRefresh: () => void;
  onClearMonthSelection?: () => void;
  /** YYYY-MM-DD → count map; days with events get a dot in the mini-month picker. */
  appointmentCounts?: Map<string, number>;
  weekStart?: WeekStart;
}

const ZOOM_LEVELS: (1 | 2 | 4)[] = [1, 2, 4];

export function CalendarToolbar({
  currentDate,
  setCurrentDate,
  viewPeriod,
  setViewPeriod,
  viewMode,
  setViewMode,
  zoom,
  setZoom,
  loading,
  onRefresh,
  onClearMonthSelection,
  appointmentCounts,
  weekStart = 0,
}: Props) {
  const [pickerAnchor, setPickerAnchor] = useState<DOMRect | null>(null);
  const dateBtnRef = useRef<HTMLButtonElement>(null);
  const isMobile = useIsMobile();
  function addDays(date: Date, n: number) {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
  }
  function addMonths(date: Date, n: number) {
    const d = new Date(date);
    d.setMonth(d.getMonth() + n);
    return d;
  }

  const goBack = () => {
    if (viewPeriod === "day") setCurrentDate(addDays(currentDate, -1));
    else if (viewPeriod === "week" || viewPeriod === "agenda") setCurrentDate(addDays(currentDate, -7));
    else setCurrentDate(addMonths(currentDate, -1));
  };
  const goForward = () => {
    if (viewPeriod === "day") setCurrentDate(addDays(currentDate, 1));
    else if (viewPeriod === "week" || viewPeriod === "agenda") setCurrentDate(addDays(currentDate, 7));
    else setCurrentDate(addMonths(currentDate, 1));
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "0.75rem",
        marginBottom: "1.25rem",
      }}
    >
      {/* Date navigation */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <button onClick={goBack} className="zd-icon-btn" aria-label="Previous" title="Previous">
          <ChevronLeftIcon size={16} />
        </button>
        <button
          ref={dateBtnRef}
          onClick={() => {
            if (dateBtnRef.current) setPickerAnchor(dateBtnRef.current.getBoundingClientRect());
          }}
          style={{
            fontFamily: "'Source Serif 4', Georgia, serif",
            fontSize: isMobile ? 15 : 18,
            fontWeight: 500,
            letterSpacing: "-0.012em",
            color: "var(--ink)",
            background: "none",
            border: "none",
            cursor: "pointer",
            textAlign: "center",
            padding: "4px 12px",
            borderRadius: 8,
            minWidth: isMobile ? 0 : 200,
            transition: "background 120ms ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--paper-well)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
          title="Pick a date"
        >
          {formatDateLabel(currentDate, viewPeriod)}
        </button>
        <button onClick={goForward} className="zd-icon-btn" aria-label="Next" title="Next">
          <ChevronRightIcon size={16} />
        </button>
        <button
          type="button"
          onClick={() => setCurrentDate(new Date())}
          className="btn btn-ghost"
          style={{ fontSize: "0.75rem", marginLeft: 6 }}
          title="Today (T)"
        >
          Today
        </button>
      </div>

      {pickerAnchor && (
        <MiniMonthPicker
          value={currentDate}
          onChange={setCurrentDate}
          onClose={() => setPickerAnchor(null)}
          anchor={pickerAnchor}
          appointmentCounts={appointmentCounts}
          weekStart={weekStart}
        />
      )}

      {/* Period + view toggles */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
        <PillGroup<ViewPeriod>
          options={[
            { value: "day", label: "Day" },
            { value: "week", label: "Week" },
            { value: "month", label: "Month" },
            { value: "agenda", label: "Agenda" },
          ]}
          value={viewPeriod}
          onChange={(v) => {
            setViewPeriod(v);
            onClearMonthSelection?.();
          }}
        />
        {!isMobile && viewPeriod === "day" && (
          <PillGroup<ViewMode>
            options={[
              { value: "resource", label: "Resource" },
              { value: "list", label: "List" },
            ]}
            value={viewMode}
            onChange={setViewMode}
          />
        )}
        {!isMobile && viewPeriod === "day" && viewMode === "resource" && (
          <div
            style={{
              display: "inline-flex",
              gap: 0,
              padding: 2,
              borderRadius: 8,
              background: "var(--paper-well)",
              boxShadow: "0 0 0 1px var(--rule)",
            }}
            role="group"
            aria-label="Zoom"
          >
            <button
              type="button"
              onClick={() => {
                const idx = ZOOM_LEVELS.indexOf(zoom);
                if (idx > 0) setZoom(ZOOM_LEVELS[idx - 1]);
              }}
              disabled={zoom === 1}
              title="Zoom out"
              aria-label="Zoom out"
              style={{
                width: 28,
                height: 26,
                background: "transparent",
                border: "none",
                color: zoom === 1 ? "var(--ink-faint)" : "var(--ink-soft)",
                fontSize: 16,
                cursor: zoom === 1 ? "default" : "pointer",
                borderRadius: 6,
              }}
            >
              −
            </button>
            <span
              style={{
                minWidth: 28,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontVariantNumeric: "tabular-nums",
                color: "var(--ink-soft)",
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              }}
            >
              {zoom}×
            </span>
            <button
              type="button"
              onClick={() => {
                const idx = ZOOM_LEVELS.indexOf(zoom);
                if (idx < ZOOM_LEVELS.length - 1) setZoom(ZOOM_LEVELS[idx + 1]);
              }}
              disabled={zoom === 4}
              title="Zoom in"
              aria-label="Zoom in"
              style={{
                width: 28,
                height: 26,
                background: "transparent",
                border: "none",
                color: zoom === 4 ? "var(--ink-faint)" : "var(--ink-soft)",
                fontSize: 16,
                cursor: zoom === 4 ? "default" : "pointer",
                borderRadius: 6,
              }}
            >
              +
            </button>
          </div>
        )}
        {!isMobile && (
          <>
            <button onClick={onRefresh} className="btn btn-ghost" style={{ fontSize: "0.8125rem" }}>
              {loading ? "..." : "Refresh"}
            </button>
            <button
              onClick={() => window.print()}
              className="btn btn-ghost"
              style={{ fontSize: "0.8125rem" }}
              title="Print this view"
              aria-label="Print"
            >
              Print
            </button>
          </>
        )}
      </div>
    </div>
  );
}
